/**
 * Wire contracts for the ops Trace ships under the `TRACE_interactivity`
 * glTF extension — the single source for:
 *
 *   - the op name used on the wire (`trace/setActive` → `setActive`,
 *     `pages/next` → `pagesNext`), and its inverse for import;
 *   - the value sockets each op declares (`inputValueSockets` /
 *     `outputValueSockets`), which the ratified spec requires on every
 *     extension-op declaration and which Babylon compares, type for type,
 *     against a mapping's `declarationSchema`;
 *   - the default each input takes when it is not wired, so the exporter can
 *     satisfy the spec's "every declared input socket has a source" rule with
 *     the value the runtime block would have used anyway.
 *
 * Socket names and types come from the block catalog (the editor/doc source
 * of truth); the defaults mirror each block's `registerDataInput` default.
 * A test pins both against the runtime.
 */

import { BLOCK_DEFS, type SocketType } from './blockCatalog';

/** The glTF extension namespace for Trace interactivity ops. */
export const TRACE_INTERACTIVITY_EXTENSION = 'TRACE_interactivity';

/**
 * Socket type on the wire. Catalog `string`/`color` are custom types defined
 * by TRACE_interactivity; `any` is polymorphic — resolved per node at export
 * (see GraphDataToKHR) and never part of a registered `declarationSchema`.
 */
export type TraceSocketType = SocketType;

export interface TraceOpContract {
    /** Internal GraphData op, e.g. `trace/setActive`. */
    graphOp: string;
    /** Op name under the TRACE_interactivity extension, e.g. `setActive`. */
    wireOp: string;
    inputs: Record<string, TraceSocketType>;
    outputs: Record<string, TraceSocketType>;
    /**
     * Default for each input when unwired: the component array to emit. An
     * input absent here takes its type default (an inline literal without
     * `value`).
     */
    defaults: Record<string, unknown[]>;
}

/**
 * Block defaults for unwired inputs — what each block's `registerDataInput`
 * resolves to when nothing is connected (an explicit `undefined` there falls
 * back to the RichType default, e.g. playVideo.play → false). Pinned against
 * the blocks by behaviorgraph-khr-conformance.test.ts.
 */
const INPUT_DEFAULTS: Record<string, Record<string, unknown[]>> = {
    'trace/onProximity': { threshold: [5] },
    'trace/setActive': { state: [false] },
    'trace/setBehaviorEnabled': { enabled: [true] },
    'trace/setActiveChild': { index: [0] },
    'trace/setState': { state: [''] },
    'trace/playAnimation': { speed: [1], loop: [false] },
    'trace/playAnimationExternal': { speed: [1], loop: [false] },
    'trace/playVideo': { play: [false], volume: [-1], seek: [-1] },
    'trace/playMedia': { src: [''], volume: [-1], seek: [-1] },
    'trace/seekMedia': { time: [0] },
    'trace/setMediaVolume': { volume: [1] },
    'trace/navigate': { url: [''] },
    'pages/next': { wrap: [true], animate: [false] },
    'pages/prev': { wrap: [true], animate: [false] },
    'pointer/interpolate': { duration: [0] },
};

/** `trace/setActive` → `setActive`; `pages/next` → `pagesNext`; `pointer/set` → `pointerSet`. */
export function traceWireOpName(graphOp: string): string {
    if (graphOp.startsWith('trace/')) return graphOp.slice('trace/'.length);
    const [domain, name] = graphOp.split('/');
    return name ? domain + name.charAt(0).toUpperCase() + name.slice(1) : graphOp;
}

function buildContracts(): Map<string, TraceOpContract> {
    const contracts = new Map<string, TraceOpContract>();
    for (const def of BLOCK_DEFS) {
        if (def.registration !== 'trace-extension') continue;
        const inputs: Record<string, TraceSocketType> = {};
        for (const s of def.dataInputs) inputs[s.name] = s.type;
        const outputs: Record<string, TraceSocketType> = {};
        for (const s of def.dataOutputs) outputs[s.name] = s.type;
        const defaults = INPUT_DEFAULTS[def.op] ?? {};
        contracts.set(def.op, { graphOp: def.op, wireOp: traceWireOpName(def.op), inputs, outputs, defaults });
    }
    return contracts;
}

const CONTRACTS = buildContracts();
const BY_WIRE_OP = new Map([...CONTRACTS.values()].map(c => [c.wireOp, c]));

/** Contract for an internal op that is exported under TRACE_interactivity, if any. */
export function traceOpContract(graphOp: string): TraceOpContract | undefined {
    return CONTRACTS.get(graphOp);
}

/** Contract for a TRACE_interactivity wire op name (`setActive`, `pagesNext`, …). */
export function traceOpContractForWireOp(wireOp: string): TraceOpContract | undefined {
    return BY_WIRE_OP.get(wireOp);
}

/** Every Trace extension op contract, in catalog order. */
export function allTraceOpContracts(): TraceOpContract[] {
    return [...CONTRACTS.values()];
}

/** KHR socket type string (`declarationSchema` vocabulary) for a fixed-typed socket; undefined for `any`. */
export function khrSocketSignature(type: TraceSocketType): 'bool' | 'int' | 'float' | 'float2' | 'float3' | 'float4' | 'custom' | undefined {
    switch (type) {
        case 'string':
        case 'color':
            return 'custom';
        case 'any':
            return undefined;
        default:
            return type;
    }
}

/** True when every socket of the op has a fixed type (so it can carry a `declarationSchema`). */
export function isMonomorphic(contract: TraceOpContract): boolean {
    return [...Object.values(contract.inputs), ...Object.values(contract.outputs)].every(t => t !== 'any');
}

/** The `declarationSchema` for a monomorphic contract (undefined for polymorphic ones). */
export function traceDeclarationSchema(contract: TraceOpContract): {
    inputValueSockets: Record<string, 'bool' | 'int' | 'float' | 'float2' | 'float3' | 'float4' | 'custom'>;
    outputValueSockets: Record<string, 'bool' | 'int' | 'float' | 'float2' | 'float3' | 'float4' | 'custom'>;
} | undefined {
    if (!isMonomorphic(contract)) return undefined;
    const map = (sockets: Record<string, TraceSocketType>) => Object.fromEntries(
        Object.entries(sockets).map(([name, type]) => [name, khrSocketSignature(type)!]),
    );
    return { inputValueSockets: map(contract.inputs), outputValueSockets: map(contract.outputs) };
}
